import { RiFileExcel2Line } from "react-icons/ri";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import {
  Button,
  DateInput,
  DropDownInput,
  InputField,
  RadioInput,
  DataTable,
} from "@/components";
import ColumnHelper from "@/utils/ColumnHelper";
import { useFilteredData, usePersistentFilters } from "@/";
import { SpaceWrapper } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { LuRefreshCw } from "react-icons/lu";
import { fetchBookedOrders, selectAllBookedOrders } from "../../app/Slices";
import { subDays } from "date-fns";
const BookedOrder = () => {
  // General state
  const [selectedRows, setSelectedRows] = useState([]);
  const dateRange =
    useSelector((state) => state.dashboard?.data?.dateRange) || {};
  const currentDate = new Date();
  const startOfMonthDate = dateRange?.startDate
    ? new Date(dateRange.startDate)
    : subDays(currentDate, 30); // Last 30 days
  const endOfMonthDate = dateRange?.endDate
    ? new Date(dateRange.endDate)
    : currentDate;
  const dispatch = useDispatch();
  // Use persistent filters hook - filters automatically persist in URL
  const [filters, setFilters] = usePersistentFilters({
    orderId: "",
    customerName: "",
    customerId: "",
    channelName: "",
    paymentMode: "",
    date: { from: startOfMonthDate, to: endOfMonthDate },
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [resetPageCounter, setResetPageCounter] = useState(0);

  // General filter configuration.
  const filterConfig = useMemo(
    () => ({
      orderId: {
        type: "comma-api",
        itemKey: "orderId",
        model: "Order",
        endpoint: "/common/search",
      },
      customerName: {
        type: "partial",
        itemKey: "user.name",
      },
      customerId: {
        type: "comma",
        itemKey: "user.clientId",
      },
      channelName: { type: "exact", itemKey: "channel" },
      paymentMode: { type: "exact", itemKey: "paymentMode" },
      date: { type: "date", itemKey: "orderDate" },
    }),
    []
  );

  // Tab filter configuration as a serializable object.

  const fetchData = async () => {
    const response = await dispatch(
      fetchBookedOrders({
        startDate: filters.date.from,
        endDate: filters.date.to,
      })
    );
  };

  useEffect(() => {
    fetchData();
  }, [appliedFilters.date, dispatch]);
  // Fetch raw data.
  const orders = useSelector(selectAllBookedOrders) || [];
  const isOrderLoading = useSelector((state) => state.order?.loading) || false;
  // Use the combined asynchronous filtering hook.
  const { data: filteredData, isLoading } = useFilteredData(
    orders,
    appliedFilters,
    filterConfig
  );
  const isLoadingData = isLoading || isOrderLoading;

  // Format currency values
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value);
  }, []);

  // Format date values
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  // Table columns - Updated for industry standards with proper tooltip handling
  const columns = useMemo(
    () => [
      ColumnHelper({
        header: "Customer Name",
        accessorKey: "user.name",
        meta: {
          tooltip: (row) => row?.user?.name || "N/A",
        },
      }),
      ColumnHelper({
        header: "Customer ID",
        accessorKey: "user.clientId",
        meta: {
          tooltip: (row) => row?.user?.clientId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Channel",
        accessorKey: "channel",
        cell: (info) => info.row.original.channel || "N/A",
        meta: {
          tooltip: (row) => row.channel || "N/A",
        },
      }),
      ColumnHelper({
        header: "Order ID",
        accessorKey: "orderId",
        meta: {
          tooltip: (row) => row?.orderId || "N/A",
        },
      }),
      ColumnHelper({
        header: "Order Date",
        accessorKey: "orderDate",
        cell: (info) => formatDate(info.row.original.orderDate),
        meta: {
          tooltip: (row) => formatDate(row?.orderDate || "N/A"),
        },
      }),
      ColumnHelper({
        header: "Product",
        accessorKey: "products",
        cell: (info) => {
          const products = info.row.original.products
            .map((product) => product.productName)
            .join(", ");
          return <span className="font-nunito">{products}</span>;
        },
        meta: {
          tooltip: (row) => row.products.map((p) => p.productName).join(", "),
        },
      }),
      ColumnHelper({
        header: "Payment",
        accessorKey: "totalOrderValue",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalOrderValue)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalOrderValue),
        },
      }),
      ColumnHelper({
        header: "Collectable Amount",
        accessorKey: "collectableAmount",
        cell: (info) => (
          <span className="font-nunito">
            {formatCurrency(info.row.original.totalOrderValue)}
          </span>
        ),
        meta: {
          tooltip: (row) => formatCurrency(row.totalOrderValue),
        },
      }),
      ColumnHelper({
        header: "Payment Mode",
        accessorKey: "paymentMode",
        meta: {
          tooltip: (row) => row.paymentMode,
        },
      }),
      ColumnHelper({
        header: "Client Name",
        accessorKey: "deliveryAddress.customerName",
        cell: (info) => info.row.original.deliveryAddress.customerName,
        meta: {
          tooltip: (row) => row.deliveryAddress.customerName,
        },
      }),
      ColumnHelper({
        header: "Zip Code",
        accessorKey: "deliveryAddress.pincode",
        cell: (info) => info.row.original.deliveryAddress.pincode,
        meta: {
          tooltip: (row) => row.deliveryAddress.pincode,
        },
      }),
      ColumnHelper({
        header: "Weight",
        accessorKey: "physicalWeight",
        cell: (info) => `${info.row.original.physicalWeight} kg`,
        meta: {
          tooltip: (row) => `${row.physicalWeight} kg`,
        },
      }),
    ],
    []
  );
  const handleChange = useCallback(
    (key, value) => {
      setFilters({ [key]: value });
    },
    [setFilters]
  );

  const handleDateChange = useCallback(
    (dateRange) => {
      setFilters({
        date: { from: dateRange?.from || "", to: dateRange?.to || "" },
      });
    },
    [setFilters]
  );

  const handleApply = useCallback(() => {
    console.log("Filters applied:", filters);
    setAppliedFilters(filters);
    setResetPageCounter((prev) => prev + 1);
  }, [filters]);

  const handleReset = useCallback(() => {
    const initialFilters = {
      orderId: "",
      channelName: "",
      paymentMode: "",
      customerName: "",
      customerId: "",
      date: { from: startOfMonthDate, to: endOfMonthDate },
    };
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setResetPageCounter((prev) => prev + 1);
  }, [setFilters, startOfMonthDate, endOfMonthDate]);

  const isRowSelectable = useCallback((rowData) => {
    return (
      rowData.status === "Not Booked" &&
      rowData.exceptionMessage.length === 1 &&
      rowData.exceptionMessage.some(
        (exception) => exception.code === "COURIER_NOT_AVAILABLE"
      )
    );
  }, []);
  const handleRefresh = () => {
    fetchData();
  };

  return (
    <SpaceWrapper>
      <div className="w-full flex-1 flex flex-col gap-4 p-4 pb-20 md:pb-4 relative max-w-[2000px]">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row gap-2 justify-between">
          <h2 className="text-headline3 lg:text-headline2 text-secondary/95 font-nunito font-bold">
            Booked Orders
          </h2>
          <div className="flex gap-2  ">
            <Button
              variant="default"
              size="sm"
              icon={LuRefreshCw}
              onClick={handleRefresh}
              isLoading={isOrderLoading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DateInput
            label="Date Filter"
            placeholder="Select start & end date"
            mode="range"
            value={filters.date}
            onChange={handleDateChange}
          />
          <InputField
            label="Order ID"
            placeholder="Order ID Comma Separated"
            value={filters.orderId}
            onChange={(val) => handleChange("orderId", val || "")}
          />
          <InputField
            label="Customer ID"
            placeholder="Enter Customer ID"
            value={filters.customerId}
            onChange={(val) => handleChange("customerId", val || "")}
          />
          <InputField
            label="Customer Name"
            placeholder="Enter Customer Name"
            value={filters.customerName}
            onChange={(val) => handleChange("customerName", val || "")}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-center">
          <DropDownInput
            label="Channel Name"
            placeholder="Select Channel Name"
            options={[
              { value: "Amazon", label: "Amazon" },
              { value: "Shopify", label: "Shopify" },
              { value: "WooCommerce", label: "WooCommerce" },
              { value: "Custom", label: "Custom" },
            ]}
            value={filters.channelName}
            onChange={(val) => handleChange("channelName", val)}
          />
          <RadioInput
            label="Payment Mode"
            name="payment_mode"
            options={[
              { value: "Prepaid", label: "Prepaid" },
              { value: "COD", label: "COD" },
            ]}
            selectedValue={filters.paymentMode}
            onChange={(val) => handleChange("paymentMode", val)}
          />
          <div className="flex flex-row gap-2 justify-end md:justify-start items-end">
            <Button
              variant="default"
              size="md"
              onClick={handleApply}
              className="h-12"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              size="md"
              onClick={handleReset}
              className="h-12"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* Tabs */}

        {/* DataTable */}
        <DataTable
          columns={columns}
          tittle="Booked Orders"
          data={filteredData}
          getSelectedRows={setSelectedRows}
          resetPage={resetPageCounter}
          isLoading={isLoadingData}
          showCheckbox={false}
          actions={[
            {
              name: "Export",
              onClick: () => {},
              icon: RiFileExcel2Line,
              body: {
                model: "Admin: Export orders",
                filename: `booked_orders${new Date().getTime()}`,
              },
            },
          ]}
          isRowSelectable={isRowSelectable}
        />
      </div>
    </SpaceWrapper>
  );
};

export default BookedOrder;
